// AGC: VoiceTelemetry — module-scoped singleton that owns the current voice session's
// AGC: attempt_number, start time, and target entity type. One active session at a time.
import { track, setFlowContext, clearFlowContext } from './analytics'
import type { EntityType, VoiceErrorCode } from './analytics-events'

interface SessionState {
  startedAt: number
  attemptNumber: number
  entityType?: EntityType
  screen: string
  flowId: string
}

let session: SessionState | null = null

function endSession(): void {
  clearFlowContext()
  session = null
}

export const VoiceTelemetry = {
  start(entityType: EntityType | undefined, screen: string): void {
    const flowId = setFlowContext(screen)
    session = {
      startedAt: Date.now(),
      attemptNumber: 1,
      entityType,
      screen,
      flowId,
    }
    void track('voice_session_start', {
      entity_type: entityType,
      context_screen: screen,
    })
  },

  retry(): void {
    if (!session) return
    session.attemptNumber += 1
  },

  async success(entityType: EntityType): Promise<void> {
    if (!session) return
    const duration = Date.now() - session.startedAt
    await track('voice_session_success', {
      entity_type: entityType,
      attempt_number: session.attemptNumber,
      duration_ms: duration,
    })
    endSession()
  },

  async fail(errorCode: VoiceErrorCode): Promise<void> {
    if (!session) return
    const duration = Date.now() - session.startedAt
    await track('voice_session_fail', {
      entity_type: session.entityType,
      attempt_number: session.attemptNumber,
      error_code: errorCode,
      duration_ms: duration,
    })
    endSession()
  },

  async cancel(): Promise<void> {
    await VoiceTelemetry.fail('user_canceled')
  },

  _resetForTests(): void {
    endSession()
  },
}
